import { Box } from "@mantine/core";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { getPropertyOptions } from "#modules/similarPart/domain/getPropertyOptions";
import { FilterLine } from "#modules/similarPart/view/filters/FilterLine";
import { Icon } from "#shared/icons/Icon";
import { Flex } from "#shared/view/components/Flex";
import { Space } from "#shared/view/components/Space";
import { Text } from "#shared/view/components/Text";
import { Button } from "#shared/view/designSystem/Button";
import { FilterQuery } from "shared";

type Props = {
  onUpdateFilters: (params: FilterQuery) => void;
  filtersParams: FilterQuery | undefined;
  hasDimensionsFilter: boolean;
};

export const SimilarPartFilters = ({
  onUpdateFilters,
  filtersParams,
  hasDimensionsFilter,
}: Props) => {
  const { t } = useTranslation();
  const formMethods = useForm<FilterQuery>({
    defaultValues: filtersParams,
  });

  const { control, handleSubmit } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: "filterGroup.expressions",
  });

  const filterGroup = useWatch({ control, name: "filterGroup" });

  const propertyOptions = getPropertyOptions({ hasDimensionsFilter });

  const findNextAvailableFilter = () => {
    const currentProperties = filterGroup.expressions.map((expression) => expression.property);

    const propertiesNotUsed = propertyOptions.filter(
      (property) => !currentProperties.includes(property.value),
    );

    const hasPropertiesNotUsed = propertiesNotUsed.length > 0;

    if (hasPropertiesNotUsed) {
      const nextFilter = propertiesNotUsed[0];

      return nextFilter;
    } else {
      const nextFilter = propertyOptions[0];

      return nextFilter;
    }
  };

  const addFilter = () => {
    const nextFilter = findNextAvailableFilter();

    if (!nextFilter || !nextFilter.operators[0]) {
      return;
    }

    append({
      property: nextFilter.value,
      operator: nextFilter.operators[0],
      value: null,
      id: uuid(),
    });
  };

  const removeFilter = (index: number) => {
    remove(index);
  };

  return (
    <Flex direction={"column"}>
      <Text>{t("similarPart.filter.when")}</Text>
      <Space h="xs" />
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onUpdateFilters)}>
          {fields.map((expression, index) => {
            return (
              <Box key={expression.id}>
                {index > 0 && <Text>{t("similarPart.filter.and")}</Text>}

                <FilterLine
                  expression={expression}
                  expressionIndex={index}
                  removeFilter={removeFilter}
                  hasDimensionsFilter={hasDimensionsFilter}
                />
              </Box>
            );
          })}
          <Space h="sm" />
          <Flex>
            <Button onClick={addFilter} variant="outline">
              {t("similarPart.filter.addFilter")}
            </Button>
          </Flex>
          <Space h="xs" />
          <Flex justify={"space-between"}>
            <Button type="submit" variant="filled" icon={<Icon name="search" />}>
              {t("similarPart.filter.search")}
            </Button>
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};
